import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, CircularProgress,
  IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow,
  Tooltip, Typography, Paper
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import { useQuery, useMutation } from '@tanstack/react-query'
import { tradesApi, exportApi } from '../../services/api'
import { usePortfolioStore } from '../../store/portfolioStore'

function PnlChip({ value }: { value: number | null }) {
  if (value === null || value === undefined) return <Chip label="Open" size="small" />
  return (
    <Chip
      label={`$${Number(value).toFixed(2)}`}
      color={value >= 0 ? 'success' : 'error'}
      size="small"
    />
  )
}

export default function TradeList() {
  const navigate = useNavigate()
  const { selectedPortfolioId } = usePortfolioStore()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [exportMsg, setExportMsg] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['trades', selectedPortfolioId, page, rowsPerPage],
    queryFn: () =>
      tradesApi
        .list({
          portfolioId: selectedPortfolioId ?? undefined,
          page,
          size: rowsPerPage,
        })
        .then((r) => r.data),
  })

  const exportMutation = useMutation({
    mutationFn: () => exportApi.startCsv().then((r) => r.data),
    onSuccess: (job) => setExportMsg(`Export job started (ID: ${job.id}). Check back shortly.`),
    onError: () => setExportMsg('Export failed to start'),
  })

  const trades = data?.content ?? []
  const total  = data?.totalElements ?? 0

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Trades</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Export to CSV">
            <IconButton onClick={() => exportMutation.mutate()} disabled={exportMutation.isPending}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate('/trades/new')}
          >
            New Trade
          </Button>
        </Box>
      </Box>

      {exportMsg && (
        <Alert severity="info" onClose={() => setExportMsg(null)} sx={{ mb: 2 }}>
          {exportMsg}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load trades</Alert>}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Entry</TableCell>
                <TableCell align="right">Exit</TableCell>
                <TableCell align="right">P&L</TableCell>
                <TableCell>Entry Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No trades found
                  </TableCell>
                </TableRow>
              ) : (
                trades.map((t: Record<string, unknown>) => (
                  <TableRow key={t.id as string} hover>
                    <TableCell>{t.symbol as string}</TableCell>
                    <TableCell>
                      <Chip
                        label={t.direction as string}
                        color={(t.direction as string) === 'LONG' ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{t.status as string}</TableCell>
                    <TableCell align="right">{Number(t.quantity).toLocaleString()}</TableCell>
                    <TableCell align="right">{Number(t.entryPrice).toFixed(4)}</TableCell>
                    <TableCell align="right">
                      {t.exitPrice ? Number(t.exitPrice).toFixed(4) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <PnlChip value={t.pnlRealized as number | null} />
                    </TableCell>
                    <TableCell>
                      {t.entryTime
                        ? new Date(t.entryTime as string).toLocaleString()
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </TableContainer>
      )}
    </Box>
  )
}
